import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Text } from '@mantine/core';
import moment from 'moment';
import { formatRupiahInput } from '../../utils/format';
import BannerRuleSubsidi from './BannerRuleSubsidi';
import FormFieldsSubsidi from './FormFieldsSubsidi';
import ModalWarningSubsidi from './ModalWarningSubsidi';
import styles from './styles.scoped.css';

export default function ModalFormSubsidi({
  opened,
  onClose,
  onSubmit,
  isLoading,
  historyList = [],
}) {
  const [nilai, setNilai] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [warningData, setWarningData] = useState(null);

  const scheduledItem = historyList?.find(
    (item) => item.status === 'SCHEDULED' || item.status === 'Belum Aktif'
  );
  const hasBelumAktif = Boolean(scheduledItem);

  const activeItem = historyList?.find(
    (item) => item.status === 'ACTIVE' || item.status === 'Aktif'
  );

  const today = moment().startOf('day').toDate();
  const scheduledStartDate = scheduledItem?.startDate || scheduledItem?.tanggalMulai;
  const maxEndDate = scheduledStartDate && moment(scheduledStartDate).isValid()
    ? moment(scheduledStartDate).subtract(1, 'day').endOf('day').toDate()
    : undefined;

  const minStartDate = today;
  const maxStartDate = hasBelumAktif ? today : undefined;

  const minEndDate = startDate ? moment(startDate).startOf('day').toDate() : today;

  const handleClose = () => {
    setNilai('');
    setStartDate(null);
    setEndDate(null);
    setIsConfirmed(false);
    setWarningModalOpen(false);
    setWarningData(null);
    onClose();
  };

  useEffect(() => {
    if (!opened) {
      setNilai('');
      setStartDate(null);
      setEndDate(null);
      setIsConfirmed(false);
      setWarningModalOpen(false);
      setWarningData(null);
    } else if (hasBelumAktif) {
      setStartDate(new Date());
    }
  }, [opened, hasBelumAktif]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (endDate && date && moment(endDate).isBefore(moment(date), 'day')) {
      setEndDate(null);
    }
  };

  const handleNilaiChange = (e) => {
    const rawVal = e.target.value;
    const cleanVal = rawVal.replace(/\D/g, '').slice(0, 6);
    setNilai(formatRupiahInput(cleanVal));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const numericNilai = parseInt(nilai.replace(/\./g, ''), 10) || 0;
    const isNilaiInvalid = !nilai || numericNilai < 1000 || numericNilai > 999999;
    if (isNilaiInvalid || !startDate || !endDate || !isConfirmed) return;

    const payload = {
      subsidyAmount: numericNilai,
      startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
      endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
    };

    const activeEndDate = activeItem?.endDate || activeItem?.tanggalBerakhir;
    const scheduledStartDate = scheduledItem?.startDate || scheduledItem?.tanggalMulai;

    let isReplace = false;
    let isGapBefore = false;
    let isGapAfter = false;

    if (activeEndDate && startDate) {
      const prevEnd = moment(activeEndDate).startOf('day');
      const newStart = moment(startDate).startOf('day');
      const expectedStart = moment(prevEnd).add(1, 'day');

      if (newStart.isSame(expectedStart, 'day') || newStart.isBefore(expectedStart, 'day')) {
        isReplace = true;
      } else {
        isGapBefore = true;
      }
    }

    if (scheduledStartDate && endDate) {
      const schedStart = moment(scheduledStartDate).startOf('day');
      const newEnd = moment(endDate).startOf('day');
      const expectedEnd = moment(schedStart).subtract(1, 'day');

      if (newEnd.isBefore(expectedEnd, 'day')) {
        isGapAfter = true;
      }
    }

    if (isReplace && isGapAfter) {
      const gapStart = moment(endDate).add(1, 'day').toDate();
      const gapEnd = moment(scheduledStartDate).subtract(1, 'day').toDate();
      setWarningData({
        endDate,
        gapEndDate: gapEnd,
        gapStartDate: gapStart,
        payload,
        previousEndDate: activeEndDate,
        startDate,
        type: 'replace_gap',
      });
      setWarningModalOpen(true);
      return;
    }

    if (isReplace) {
      setWarningData({
        endDate,
        payload,
        previousEndDate: activeEndDate,
        startDate,
        type: 'replace',
      });
      setWarningModalOpen(true);
      return;
    }

    if (isGapBefore) {
      const prevEnd = moment(activeEndDate).startOf('day');
      const expectedStart = moment(prevEnd).add(1, 'day');
      setWarningData({
        endDate,
        gapEndDate: moment(startDate).subtract(1, 'day').toDate(),
        gapStartDate: expectedStart.toDate(),
        payload,
        previousEndDate: activeEndDate,
        startDate,
        type: 'gap',
      });
      setWarningModalOpen(true);
      return;
    }

    if (isGapAfter) {
      const gapStart = moment(endDate).add(1, 'day').toDate();
      const gapEnd = moment(scheduledStartDate).subtract(1, 'day').toDate();
      setWarningData({
        endDate,
        gapEndDate: gapEnd,
        gapStartDate: gapStart,
        payload,
        startDate,
        type: 'gap',
      });
      setWarningModalOpen(true);
      return;
    }

    onSubmit(payload);
  };

  const handleConfirmWarning = () => {
    const payload = warningData?.payload;
    setWarningModalOpen(false);
    setWarningData(null);
    onClose();
    if (payload) {
      onSubmit(payload);
    }
  };

  const handleKoreksiWarning = () => {
    setWarningModalOpen(false);
  };

  const numericNilai = parseInt(nilai.replace(/\./g, ''), 10) || 0;
  const isNilaiValid = Boolean(nilai) && numericNilai >= 1000 && numericNilai <= 999999;
  const isFormValid = isNilaiValid && startDate && endDate && isConfirmed;

  let nilaiError = '';
  if (nilai && numericNilai === 0) {
    nilaiError = 'Tidak bisa memasukan harga 0';
  }
  const RpPrefix = <Text c="dimmed" fw={600} fz="sm">Rp</Text>;
  const isModalOpened = opened && !warningModalOpen;

  return (
    <>
      <Modal
        centered
        onClose={handleClose}
        opened={isModalOpened}
        radius="lg"
        size="lg"
        styles={{
          body: {
            padding: '8px 24px 24px 24px',
          },
          header: {
            alignItems: 'center',
            padding: '20px 24px 0 24px',
          },
          title: {
            color: '#25282b',
            fontSize: '20px',
            fontWeight: 700,
          },
        }}
        title="Tambah Informasi Besaran Subsidi"
        transitionProps={{ duration: 0 }}
        withCloseButton
      >
        <div>
          <p className={styles.modalSubtitle}>
            Mengatur informasi besaran subsidi per tabung 3 Kg.
          </p>

          <BannerRuleSubsidi
            size={40}
            style={{ marginBottom: '20px' }}
            text="Informasi besaran subsidi akan otomatis menonaktifkan informasi besaran subsidi sebelumnya."
            type="info"
          />

          <FormFieldsSubsidi
            endDate={endDate}
            handleClose={handleClose}
            handleNilaiChange={handleNilaiChange}
            handleStartDateChange={handleStartDateChange}
            handleSubmit={handleSubmit}
            isConfirmed={isConfirmed}
            isFormValid={Boolean(isFormValid)}
            isLoading={isLoading}
            isStartDateDisabled={hasBelumAktif}
            maxEndDate={maxEndDate}
            maxStartDate={maxStartDate}
            minEndDate={minEndDate}
            minStartDate={minStartDate}
            nilai={nilai}
            nilaiError={nilaiError}
            RpPrefix={RpPrefix}
            setEndDate={setEndDate}
            setIsConfirmed={setIsConfirmed}
            startDate={startDate}
          />
        </div>
      </Modal>

      {warningData && (
        <ModalWarningSubsidi
          endDate={warningData.endDate}
          gapEndDate={warningData.gapEndDate}
          gapStartDate={warningData.gapStartDate}
          isLoading={isLoading}
          onClose={() => setWarningModalOpen(false)}
          onConfirm={handleConfirmWarning}
          onKoreksi={handleKoreksiWarning}
          opened={warningModalOpen}
          previousEndDate={warningData.previousEndDate}
          startDate={warningData.startDate}
          type={warningData.type}
        />
      )}
    </>
  );
}

ModalFormSubsidi.propTypes = {
  historyList: PropTypes.array,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  opened: PropTypes.bool.isRequired,
};
